import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  type CreateProductRequest,
  type Locale,
  SHIPPING_CLASSES,
  type UpdateProductRequest,
} from '@shop/types';
import { ArrowLeft, Lock } from 'lucide-react';
import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { LocaleTabs } from '../../components/locale-tabs';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Field } from '../../components/ui/field';
import { Input, Select, Textarea } from '../../components/ui/input';
import { ApiError, api, errorMessage } from '../../lib/api';
import {
  attributesQuery,
  brandsQuery,
  categoriesQuery,
  categoryLabel,
  manufacturersQuery,
} from '../../lib/queries';
import { useCan } from '../../lib/session';
import type { Product } from '../../lib/types';
import { AiDraftButton } from '../ai-drafts';
import { toDrafts, toRequest } from './attribute-values';
import { AttributesForm } from './attributes-form';
import { STATUS_BADGE } from './list';
import { MediaSection, PublishSection, VariantsSection } from './sections';

const TAX_CATEGORIES = ['standard', 'books', 'food_basic', 'medical', 'zero'] as const;

type TextField =
  'name' | 'slug' | 'shortDescription' | 'description' | 'seoTitle' | 'seoDescription';
type TextDraft = Record<TextField, string>;
const emptyText = (): TextDraft => ({
  name: '',
  slug: '',
  shortDescription: '',
  description: '',
  seoTitle: '',
  seoDescription: '',
});

interface VariantDraft {
  sku: string;
  gtin: string;
  mpn: string;
  weightG: string;
}

export const productQuery = (id: string) => ({
  queryKey: ['catalog', 'product', id],
  queryFn: () => api<Product>(`/catalog/products/${id}`),
});

export function ProductEditPage({ productId }: { productId: string }) {
  const product = useQuery(productQuery(productId));
  if (product.error) return <Alert variant="destructive">{errorMessage(product.error)}</Alert>;
  if (!product.data) return <p className="text-muted-foreground">…</p>;
  // Новая версия с сервера — форма пересоздаётся с актуальными данными.
  return <ProductForm key={product.data.version} product={product.data} />;
}

export function ProductCreatePage() {
  return <ProductForm />;
}

function ProductForm({ product }: { product?: Product }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCan('catalog.write');
  const canDraft = useCan('ai.content_review') && canWrite;
  const categories = useQuery(categoriesQuery);
  const brands = useQuery(brandsQuery);
  const manufacturers = useQuery(manufacturersQuery);
  const attributes = useQuery(attributesQuery);

  const [locale, setLocale] = useState<Locale>('lv');
  const [main, setMain] = useState({
    brandId: product?.brandId ?? '',
    manufacturerId: product?.manufacturerId ?? '',
    primaryCategoryId: product?.primaryCategoryId ?? '',
    extraCategoryIds: (product?.categoryIds ?? []).filter(
      (id) => id !== product?.primaryCategoryId,
    ),
    taxCategory: product?.taxCategory ?? 'standard',
    shippingClass: product?.shippingClass ?? 'standard',
    warrantyMonths: product?.warrantyMonths?.toString() ?? '',
  });
  const [texts, setTexts] = useState<Partial<Record<Locale, TextDraft>>>(() => {
    const result: Partial<Record<Locale, TextDraft>> = {};
    for (const [loc, row] of Object.entries(product?.translations ?? {})) {
      result[loc as Locale] = {
        name: row.name,
        slug: row.slug,
        shortDescription: row.shortDescription ?? '',
        description: row.description ?? '',
        seoTitle: row.seoTitle ?? '',
        seoDescription: row.seoDescription ?? '',
      };
    }
    return result;
  });
  const [drafts, setDrafts] = useState(() => toDrafts(product?.attributes ?? []));
  const [variants, setVariants] = useState<VariantDraft[]>([
    { sku: '', gtin: '', mpn: '', weightG: '' },
  ]);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const text = texts[locale] ?? emptyText();
  const setText = (field: TextField, value: string) => {
    setTexts({ ...texts, [locale]: { ...text, [field]: value } });
  };
  const locked = new Set(product?.fieldSources.filter((f) => f.locked).map((f) => f.field));

  const buildTranslations = () => {
    const result: CreateProductRequest['translations'] = {};
    for (const [loc, draft] of Object.entries(texts) as [Locale, TextDraft][]) {
      if (!draft.name.trim()) continue;
      result[loc] = {
        name: draft.name.trim(),
        ...(draft.slug.trim() ? { slug: draft.slug.trim() } : {}),
        ...(draft.shortDescription ? { shortDescription: draft.shortDescription } : {}),
        ...(draft.description ? { description: draft.description } : {}),
        ...(draft.seoTitle ? { seoTitle: draft.seoTitle } : {}),
        ...(draft.seoDescription ? { seoDescription: draft.seoDescription } : {}),
      };
    }
    return result;
  };

  const save = async (e: SyntheticEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    const common = {
      brandId: main.brandId || null,
      manufacturerId: main.manufacturerId || null,
      primaryCategoryId: main.primaryCategoryId || null,
      extraCategoryIds: main.extraCategoryIds,
      taxCategory: main.taxCategory,
      shippingClass: main.shippingClass,
      translations: buildTranslations(),
      attributes: toRequest(drafts, attributes.data ?? [], product?.attributes),
    };
    try {
      if (product) {
        const body: UpdateProductRequest = {
          ...common,
          version: product.version,
          warrantyMonths: main.warrantyMonths ? Number(main.warrantyMonths) : null,
        };
        await api(`/catalog/products/${product.id}`, { method: 'PATCH', body });
        await queryClient.invalidateQueries({ queryKey: ['catalog'] });
      } else {
        const body: CreateProductRequest = {
          ...common,
          ...(main.warrantyMonths ? { warrantyMonths: Number(main.warrantyMonths) } : {}),
          variants: variants
            .filter((v) => v.sku.trim())
            .map((v) => ({
              sku: v.sku.trim(),
              ...(v.gtin.trim() ? { gtin: v.gtin.trim() } : {}),
              ...(v.mpn.trim() ? { mpn: v.mpn.trim() } : {}),
              ...(v.weightG ? { weightG: Number(v.weightG) } : {}),
            })),
        };
        const created = await api<{ id: string }>('/catalog/products', { method: 'POST', body });
        await queryClient.invalidateQueries({ queryKey: ['catalog', 'products'] });
        await navigate({ to: '/products/$productId', params: { productId: created.id } });
      }
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'version_conflict'
          ? t('products.conflict')
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const allCategories = categories.data ?? [];
  const title = product
    ? (product.translations[locale]?.name ??
      Object.values(product.translations)[0]?.name ??
      product.id)
    : t('products.new');

  return (
    <>
      <Link
        to="/products"
        className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('nav.products')}
      </Link>
      <PageHeader
        title={title}
        actions={
          product ? (
            <span className="flex items-center gap-3">
              {canDraft ? (
                <AiDraftButton
                  productId={product.id}
                  locale={locale}
                  hasName={!!product.translations[locale]?.name}
                />
              ) : null}
              <Badge variant={STATUS_BADGE[product.status]}>
                {t(`products.status.${product.status}`)}
              </Badge>
            </span>
          ) : null
        }
      />
      <div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
        <form onSubmit={(e) => void save(e)} className="grid content-start gap-6">
          <Card>
            <CardHeader
              title={t('products.translations')}
              description={t('products.translationHint')}
              actions={
                <LocaleTabs
                  value={locale}
                  onChange={setLocale}
                  filled={(l) => !!texts[l]?.name.trim()}
                />
              }
            />
            <CardContent className="grid gap-4">
              <Field label={t('products.productName')}>
                {(id) => (
                  <Input
                    id={id}
                    disabled={!canWrite}
                    value={text.name}
                    onChange={(e) => {
                      setText('name', e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label={t('products.slug')} hint={t('products.slugHint')}>
                {(id) => (
                  <Input
                    id={id}
                    disabled={!canWrite}
                    value={text.slug}
                    onChange={(e) => {
                      setText('slug', e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label={t('products.shortDescription')}>
                {(id) => (
                  <Textarea
                    id={id}
                    rows={2}
                    disabled={!canWrite}
                    value={text.shortDescription}
                    onChange={(e) => {
                      setText('shortDescription', e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label={t('products.description')}>
                {(id) => (
                  <Textarea
                    id={id}
                    rows={8}
                    disabled={!canWrite}
                    value={text.description}
                    onChange={(e) => {
                      setText('description', e.target.value);
                    }}
                  />
                )}
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('products.seoTitle')}>
                  {(id) => (
                    <Input
                      id={id}
                      disabled={!canWrite}
                      value={text.seoTitle}
                      onChange={(e) => {
                        setText('seoTitle', e.target.value);
                      }}
                    />
                  )}
                </Field>
                <Field label={t('products.seoDescription')}>
                  {(id) => (
                    <Input
                      id={id}
                      disabled={!canWrite}
                      value={text.seoDescription}
                      onChange={(e) => {
                        setText('seoDescription', e.target.value);
                      }}
                    />
                  )}
                </Field>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader title={t('products.main')} />
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label={t('products.primaryCategory')}>
                {(id) => (
                  <Select
                    id={id}
                    disabled={!canWrite}
                    value={main.primaryCategoryId}
                    onChange={(e) => {
                      setMain({ ...main, primaryCategoryId: e.target.value });
                    }}
                  >
                    <option value="">{t('common.none')}</option>
                    {allCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {categoryLabel(c, allCategories, i18n.language)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('products.extraCategories')}>
                {(id) => (
                  <Select
                    id={id}
                    multiple
                    className="h-24"
                    disabled={!canWrite}
                    value={main.extraCategoryIds}
                    onChange={(e) => {
                      setMain({
                        ...main,
                        extraCategoryIds: [...e.target.selectedOptions].map((o) => o.value),
                      });
                    }}
                  >
                    {allCategories
                      .filter((c) => c.id !== main.primaryCategoryId)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {categoryLabel(c, allCategories, i18n.language)}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
              <Field label={t('products.brand')}>
                {(id) => (
                  <Select
                    id={id}
                    disabled={!canWrite}
                    value={main.brandId}
                    onChange={(e) => {
                      setMain({ ...main, brandId: e.target.value });
                    }}
                  >
                    <option value="">{t('common.none')}</option>
                    {(brands.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('products.manufacturer')}>
                {(id) => (
                  <Select
                    id={id}
                    disabled={!canWrite}
                    value={main.manufacturerId}
                    onChange={(e) => {
                      setMain({ ...main, manufacturerId: e.target.value });
                    }}
                  >
                    <option value="">{t('common.none')}</option>
                    {(manufacturers.data ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('products.taxCategory')}>
                {(id) => (
                  <Select
                    id={id}
                    disabled={!canWrite}
                    value={main.taxCategory}
                    onChange={(e) => {
                      setMain({ ...main, taxCategory: e.target.value });
                    }}
                  >
                    {TAX_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('products.shippingClass')}>
                {(id) => (
                  <Select
                    id={id}
                    disabled={!canWrite}
                    value={main.shippingClass}
                    onChange={(e) => {
                      setMain({
                        ...main,
                        shippingClass: e.target.value as (typeof SHIPPING_CLASSES)[number],
                      });
                    }}
                  >
                    {SHIPPING_CLASSES.map((c) => (
                      <option key={c} value={c}>
                        {t(`products.shipping.${c}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('products.warranty')}>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min={0}
                    max={240}
                    disabled={!canWrite}
                    value={main.warrantyMonths}
                    onChange={(e) => {
                      setMain({ ...main, warrantyMonths: e.target.value });
                    }}
                  />
                )}
              </Field>
            </CardContent>
          </Card>

          {(attributes.data ?? []).length > 0 ? (
            <Card>
              <CardHeader
                title={t('products.attributesTitle')}
                description={t('products.attributesHint')}
              />
              <CardContent>
                <AttributesForm
                  attributes={attributes.data ?? []}
                  drafts={drafts}
                  onChange={setDrafts}
                  locale={locale}
                  disabled={!canWrite}
                />
              </CardContent>
            </Card>
          ) : null}

          {!product ? (
            <Card>
              <CardHeader title={t('products.variantsTitle')} />
              <CardContent className="grid gap-3">
                {variants.map((v, i) => (
                  <div key={i} className="grid gap-2 sm:grid-cols-4">
                    {(['sku', 'gtin', 'mpn', 'weightG'] as const).map((field) => (
                      <Input
                        key={field}
                        aria-label={t(
                          field === 'weightG' ? 'products.weight' : `products.${field}`,
                        )}
                        placeholder={t(
                          field === 'weightG' ? 'products.weight' : `products.${field}`,
                        )}
                        inputMode={field === 'sku' || field === 'mpn' ? 'text' : 'numeric'}
                        required={field === 'sku' && i === 0}
                        value={v[field]}
                        onChange={(e) => {
                          setVariants(
                            variants.map((x, j) =>
                              j === i ? { ...x, [field]: e.target.value } : x,
                            ),
                          );
                        }}
                      />
                    ))}
                  </div>
                ))}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setVariants([...variants, { sku: '', gtin: '', mpn: '', weightG: '' }]);
                    }}
                  >
                    {t('products.addVariant')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {error ? <Alert variant="destructive">{error}</Alert> : null}
          {canWrite ? (
            <div className="flex justify-end">
              <Button type="submit" disabled={busy}>
                {product ? t('common.save') : t('common.create')}
              </Button>
            </div>
          ) : null}
        </form>

        {product ? (
          <div className="grid content-start gap-6">
            <PublishSection product={product} />
            <VariantsSection product={product} />
            <MediaSection product={product} />
            {locked.size > 0 ? (
              <Card>
                <CardHeader
                  title={t('products.lockedFields')}
                  description={t('products.lockedHint')}
                />
                <CardContent className="flex flex-wrap gap-2">
                  {[...locked].map((field) => (
                    <Badge key={field} variant="outline" className="gap-1">
                      <Lock className="size-3" />
                      {field}
                    </Badge>
                  ))}
                </CardContent>
              </Card>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}
